import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Add, GripVertical, Trash } from '@/components/icons';
import {
    DEFAULT_COLUMN_WIDTHS,
    DEFAULT_HEADER_VALUES,
} from '@/components/preview/test-table-editor/constants';
import { AppDialogLayout } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createId } from '@/lib/table-utils';
import { cn } from '@/lib/utils';
import { moveColumn } from './free-instrument-columns';
import { InstrumentIdentityFields } from './InstrumentIdentityFields';
import type { DraggableProvided, DropResult } from '@hello-pangea/dnd';

/** Same width the canvas table editor gives a column added by hand. */
const NEW_COLUMN_WIDTH = 150;
const MIN_COLUMNS = 1;
/** Past this the columns are too narrow to read on A4. */
const MAX_COLUMNS = 8;

/** Single droppable, single context — the id only has to be stable. */
const COLUMNS_DROPPABLE_ID = 'free-instrument-columns';

/**
 * A column carries its own width so `columnWidths` is always derived from the
 * same array as the header cells — reordering or removing one cannot desync the
 * two, which the API's `superRefine` rejects outright.
 */
type DraftColumn = { id: string; label: string; width: number };

export type FreeInstrumentValues = {
    name: string;
    reference: string;
    columns: { label: string; width: number }[];
};

interface FreeInstrumentModalProps {
    onConfirm: (values: FreeInstrumentValues) => void;
    /** The caller passes `closeModal`, like `TestSelector`'s. */
    onClose: () => void;
    /**
     * Seeds the name. Set when the catalog handed the user over: what they
     * searched for and did not find is the name of the instrument they are
     * about to describe, so it should not have to be typed twice.
     */
    initialName?: string;
    /** Returns to whatever opened this — the catalog, with its search intact. */
    backAction?: { label: string; onClick: () => void };
}

const createDefaultColumns = (): DraftColumn[] =>
    DEFAULT_HEADER_VALUES.map((label, index) => ({
        id: createId('col'),
        label,
        width: DEFAULT_COLUMN_WIDTHS[index] ?? NEW_COLUMN_WIDTH,
    }));

interface ColumnRowProps {
    column: DraftColumn;
    index: number;
    canRemove: boolean;
    isDragging: boolean;
    provided: DraggableProvided;
    onRename: (id: string, label: string) => void;
    onRemove: (id: string) => void;
}

/**
 * One column row, rendered both in the list and — unchanged — as the drag
 * clone, so a lift cannot make the row jump or reflow.
 */
function ColumnRow({
    column,
    index,
    canRemove,
    isDragging,
    provided,
    onRename,
    onRemove,
}: ColumnRowProps) {
    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={cn(
                // Opaque on purpose: the clone is portalled to <body> and floats
                // over the dialog, so the gaps between the three controls must
                // not show the page through them.
                'flex items-center gap-2 rounded-xl bg-background',
                isDragging && 'shadow-elevation-2',
            )}
        >
            {/* `dragHandleProps` stays spread first so dnd keeps its handlers
                and its `aria-describedby` lift instructions. Full row height
                makes the narrow strip an easy grab target. */}
            <button
                type="button"
                {...provided.dragHandleProps}
                aria-label={`Reordenar coluna ${index + 1}`}
                className="flex h-10 w-6 shrink-0 cursor-grab items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
            >
                <GripVertical className="size-4" />
            </button>
            <Input
                value={column.label}
                aria-label={`Nome da coluna ${index + 1}`}
                placeholder="Ex: Percentil"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onRename(column.id, event.target.value)
                }
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remover coluna ${index + 1}`}
                disabled={!canRemove}
                onClick={() => onRemove(column.id)}
                className="shrink-0 text-muted-foreground hover:text-destructive"
            >
                <Trash className="size-4" />
            </Button>
        </div>
    );
}

/**
 * Collects what the Satepsi catalog would have supplied for an instrument that
 * is not in it: a name, a citation, and the table's columns.
 */
export default function FreeInstrumentModal({
    onConfirm,
    onClose,
    initialName,
    backAction,
}: FreeInstrumentModalProps) {
    const [name, setName] = useState(initialName ?? '');
    const [reference, setReference] = useState('');
    const [columns, setColumns] = useState<DraftColumn[]>(createDefaultColumns);

    const canSubmit = name.trim().length > 0;

    const renameColumn = (id: string, label: string) => {
        setColumns((prev) =>
            prev.map((column) => (column.id === id ? { ...column, label } : column)),
        );
    };

    const removeColumn = (id: string) => {
        setColumns((prev) =>
            prev.length <= MIN_COLUMNS ? prev : prev.filter((column) => column.id !== id),
        );
    };

    const addColumn = () => {
        setColumns((prev) =>
            prev.length >= MAX_COLUMNS
                ? prev
                : [...prev, { id: createId('col'), label: '', width: NEW_COLUMN_WIDTH }],
        );
    };

    const handleColumnDragEnd = (result: DropResult) => {
        const { source, destination } = result;
        // Dropped outside any list, or back where it started.
        if (!destination || destination.droppableId !== COLUMNS_DROPPABLE_ID) return;
        if (destination.index === source.index) return;

        setColumns((prev) => moveColumn(prev, source.index, destination.index));
    };

    const handleSubmit = (event?: FormEvent) => {
        event?.preventDefault();
        if (!canSubmit) return;

        onConfirm({
            name: name.trim(),
            reference: reference.trim(),
            columns: columns.map(({ label, width }) => ({ label: label.trim(), width })),
        });
        onClose();
    };

    return (
        <AppDialogLayout
            title="Novo instrumento livre"
            backAction={backAction}
            footer={
                <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="button" disabled={!canSubmit} onClick={() => handleSubmit()}>
                        Adicionar instrumento
                    </Button>
                </div>
            }
        >
            {/* The form stays in the body so Enter submits; the footer sits
                outside it and goes through the same handler. */}
            <form className="space-y-5" onSubmit={handleSubmit}>
                <InstrumentIdentityFields
                    idPrefix="free-instrument"
                    // Only when this dialog replaced another one in the shared
                    // slot: Radix does not re-run its open-autofocus on a swap,
                    // so without this the keyboard lands on the dialog chrome.
                    autoFocusName={Boolean(backAction)}
                    values={{ name, reference }}
                    onChange={(next) => {
                        setName(next.name);
                        setReference(next.reference);
                    }}
                    nameRequired
                />

                <div className="space-y-2">
                    <Label>Colunas da tabela</Label>
                    {/* Its own context: this modal renders from `ModalProvider`
                        at the App root — a sibling of `<Pages/>` — so the report
                        editor's `DragDropContext` sits on another branch of the
                        React tree. There is nothing to nest inside. */}
                    <DragDropContext
                        onDragEnd={handleColumnDragEnd}
                        dragHandleUsageInstructions="Pressione a barra de espaço para pegar a coluna. Use as setas para mover, a barra de espaço para soltar e Esc para cancelar."
                    >
                        <Droppable
                            droppableId={COLUMNS_DROPPABLE_ID}
                            // dnd drags with `position: fixed`, which resolves
                            // against the nearest transformed ancestor — and
                            // `DialogContent` is `translate-x/y-[-50%]`, so an
                            // in-place drag would render hundreds of pixels from
                            // the pointer. Passing `renderClone` makes dnd portal
                            // the dragged row into `document.body` itself, where
                            // `fixed` means the viewport again.
                            renderClone={(cloneProvided, cloneSnapshot, rubric) => (
                                <ColumnRow
                                    // `columns` only changes in `onDragEnd`, once
                                    // the clone is gone, so this is always in range.
                                    column={columns[rubric.source.index]}
                                    index={rubric.source.index}
                                    canRemove={columns.length > MIN_COLUMNS}
                                    isDragging={cloneSnapshot.isDragging}
                                    provided={cloneProvided}
                                    onRename={renameColumn}
                                    onRemove={removeColumn}
                                />
                            )}
                        >
                            {(droppableProvided) => (
                                <div
                                    ref={droppableProvided.innerRef}
                                    {...droppableProvided.droppableProps}
                                    // `gap`, not `space-y`: with a clone the lifted
                                    // row leaves the DOM, so under `space-y` the
                                    // next row would become the first child and
                                    // drop its margin while the placeholder
                                    // inherits the lifted row's 0 — a jump on every
                                    // lift of the top row. Gaps are immune: the
                                    // placeholder replaces the row one for one.
                                    className="flex flex-col gap-2"
                                >
                                    {columns.map((column, index) => (
                                        <Draggable
                                            key={column.id}
                                            draggableId={column.id}
                                            index={index}
                                            // The handle is a <button>, and dnd
                                            // refuses to lift from an interactive
                                            // element unless told otherwise: that
                                            // guard rejects every keyboard lift and
                                            // every pointer press landing beside the
                                            // glyph rather than on it. Safe to
                                            // disable — only the grip carries
                                            // `dragHandleProps`, so the name field
                                            // and the remove button still cannot
                                            // start a drag.
                                            disableInteractiveElementBlocking
                                        >
                                            {(draggableProvided) => (
                                                <ColumnRow
                                                    column={column}
                                                    index={index}
                                                    canRemove={columns.length > MIN_COLUMNS}
                                                    // Never true here: with a clone
                                                    // the lifted row is unmounted.
                                                    isDragging={false}
                                                    provided={draggableProvided}
                                                    onRename={renameColumn}
                                                    onRemove={removeColumn}
                                                />
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={columns.length >= MAX_COLUMNS}
                        onClick={addColumn}
                        className="w-full border-dashed"
                    >
                        <Add className="mr-2 size-4" />
                        Adicionar coluna
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        Você ainda poderá adicionar, renomear e reordenar colunas direto na tabela.
                    </p>
                </div>
            </form>
        </AppDialogLayout>
    );
}
